/**
 * THE SINGLE FEATURE FLAG.
 *
 * One boolean decides whether the app wears its current SPA chrome
 * (`components/layout/DashboardLayout.tsx` — navy TopBar + SectionNav +
 * MobileTabBar) or the Wallet-style chrome in this directory. Nothing is
 * deleted either way: `<AppShell/>` renders one or the other, and both render
 * the SAME `<Outlet/>` over the SAME registry-generated route table, so the
 * permission model is untouched by the switch.
 *
 * ─── HOW TO TURN IT ON ──────────────────────────────────────────────────────
 *
 *   default       PHONES (<768px) already get it; desktops do not
 *   per build     `VITE_WALLET_SHELL=on npm run build` forces it everywhere
 *                 (`off` forces it nowhere)
 *   per browser   visit any page with `?shell=wallet` (sticky, per browser);
 *                 `?shell=classic` goes back, `?shell=reset` forgets the
 *                 override and falls back to the build default.
 *
 * ─── HOW TO REVERT ──────────────────────────────────────────────────────────
 *
 *   `VITE_WALLET_SHELL=off` turns it off for everyone who has not set a
 *   per-browser override, without touching this file. To remove the shell from
 *   the app entirely, change the ONE line in `src/routes.tsx` back:
 *
 *       element: (<RequireAuth><AppShell /></RequireAuth>)
 *       ->
 *       element: (<RequireAuth><DashboardLayout /></RequireAuth>)
 *
 * WHY A RUNTIME OVERRIDE AND NOT JUST AN ENV VAR
 * ----------------------------------------------
 * The point of the flag is EVALUATION — the person judging this is going to
 * flip between the two shells on the same deployed build, on a phone, several
 * times. An env var costs a rebuild and a redeploy per flip; a query param
 * costs a tap. The override is per-browser (localStorage), never per-user and
 * never server-side, so it cannot leak between family members and cannot be
 * mistaken for a permission.
 *
 * The flag chooses CHROME. It never chooses ROUTES, and it never chooses
 * ACCESS — a Wallet-shell user reaches exactly the pages `PAGE_REGISTRY` and
 * `guardRoute` already allow them, no more and no fewer.
 */
import { useSyncExternalStore } from "react";

/**
 * The default when nobody has chosen: EVERY screen gets the Wallet shell.
 *
 * Changed 2026-08-26 on the owner's explicit instruction ("enable wallet shell
 * on desktop too"), replacing the phone-only default set earlier the same day.
 *
 * What this costs, recorded because it is a real trade and not a free win: on a
 * wide screen the Wallet chrome puts navigation in a bottom tab bar, which is
 * far from the pointer and spends a row that a 1440px screen has no shortage
 * of; and the destinations that do not fit the bar move behind "More" — one
 * extra click to reach Accounts, Budget, Loans, ITR, Invest, Vault and the
 * admin console, which the previous desktop navbar reached directly.
 *
 * The shell no longer depends on viewport width at all — the bottom bar's
 * thumb-sized layout on a phone versus its desktop presentation is handled by
 * CSS inside the shell itself, not by a JS media-query check here. There used
 * to be a `PHONE_QUERY`/`isPhoneViewport()` pair in this file for that; it had
 * no remaining callers and was removed rather than left as a trap for a future
 * reader who assumes it is still wired to something.
 */

/**
 * The single place a future default change would go (e.g. reverting to
 * phone-only). Kept as a named function rather than inlined at its one call
 * site below so that intent stays documented in code, not just in this file's
 * header comment.
 */
function shellDefault(): boolean {
  return true;
}


export const WALLET_SHELL_STORAGE_KEY = "aim-wallet-shell";
/** `?shell=wallet` | `?shell=classic` | `?shell=reset` */
export const WALLET_SHELL_QUERY_PARAM = "shell";

function envEnabled(): boolean | null {
  const raw = import.meta.env?.VITE_WALLET_SHELL;
  if (raw === "on" || raw === "true" || raw === "1") return true;
  if (raw === "off" || raw === "false" || raw === "0") return false;
  return null;
}

function readOverride(): boolean | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(WALLET_SHELL_STORAGE_KEY);
    if (raw === "wallet") return true;
    if (raw === "classic") return false;
    return null;
  } catch {
    return null;
  }
}

function writeOverride(value: boolean | null): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (value === null) localStorage.removeItem(WALLET_SHELL_STORAGE_KEY);
    else localStorage.setItem(WALLET_SHELL_STORAGE_KEY, value ? "wallet" : "classic");
  } catch {
    /* private browsing: the choice applies for this session only */
  }
}

/**
 * Consume `?shell=` once, at module load, BEFORE React mounts — so the first
 * paint is already the right chrome and there is no flash of the other shell.
 * The param is then stripped from the URL with `replaceState` (no navigation,
 * no history entry) because a sticky flag does not belong in a bookmark.
 */
function consumeQueryParam(): void {
  if (typeof window === "undefined" || !window.location?.search) return;
  const url = new URL(window.location.href);
  const value = url.searchParams.get(WALLET_SHELL_QUERY_PARAM);
  if (value === null) return;
  if (value === "wallet") writeOverride(true);
  else if (value === "classic") writeOverride(false);
  else if (value === "reset") writeOverride(null);
  else return; // an unrelated `?shell=` value is left alone
  url.searchParams.delete(WALLET_SHELL_QUERY_PARAM);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

consumeQueryParam();

/**
 * The EXPLICIT choice, or null for "nobody has said". Kept separate from the
 * effective answer so that resizing a window never silently overwrites a
 * decision the user actually made.
 */
let override: boolean | null = readOverride() ?? envEnabled();

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // No media subscription: the shell no longer depends on viewport width, so
  // resizing cannot change which one is shown. Re-adding a listener here would
  // fire on every resize and repaint for nothing.
  return () => {
    listeners.delete(listener);
  };
}

export function isWalletShellEnabled(): boolean {
  // Retired 2026-09-26: the classic layout is gone, so the answer is always yes.
  // `override` is still tracked only so the old tests of the store keep compiling.
  void override;
  void shellDefault;
  return true;
}

/** Remove a stored "classic" choice left by the retired layout switch. */
export function forgetClassicLayoutChoice(): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(WALLET_SHELL_STORAGE_KEY);
  } catch {
    /* storage blocked — nothing to forget */
  }
}

/**
 * Flip at runtime (the Preferences-style switch in the avatar menu). Persists
 * per browser and repaints without a reload.
 */
export function setWalletShellEnabled(next: boolean): void {
  if (next === isWalletShellEnabled() && override !== null) return;
  override = next;
  writeOverride(next);
  notify();
}

/** Forget the explicit choice and go back to "phone yes, desktop no". */
export function clearWalletShellOverride(): void {
  override = null;
  writeOverride(null);
  notify();
}

/** Reactive read — `<AppShell/>` re-renders the moment the flag flips. */
export function useWalletShellEnabled(): boolean {
  // Server snapshot matches the client default, so the first paint does not
  // flip. There is no viewport to consult during prerender and none is needed.
  return useSyncExternalStore(subscribe, isWalletShellEnabled, () => true);
}
